import { MapPin, Phone } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import SectionHead from "@/components/SectionHead";
import PresenceMap from "@/components/PresenceMap";
import Seo from "@/components/Seo";
import HEROES from "@/lib/pageHeroes.json";

// Dubai — Water Canal towers and footbridge

export default function Offices() {
  const { t, L } = useLang();
  const site = useSite();
  const s = site?.settings || {};
  const offices = site?.offices || [];

  return (
    <div data-testid="offices-page">
      <Seo page="offices" />
      <PageHero eyebrow={t("offices_eyebrow")} title={t("offices_title")} sub={t("reach_sub")} image={HEROES["offices"]} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
        <Reveal>
          <div data-testid="hq-card" className="border border-primary/50 bg-card p-9 md:p-14" style={{ borderRadius: "var(--radius)" }}>
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div>
                <p className="eyebrow mb-3">{t("hq_badge")}</p>
                <h2 className="font-head text-3xl md:text-5xl">{L(s.site_name)} — Abu Dhabi</h2>
              </div>
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <MapPin className="h-6 w-6" />
              </span>
            </div>
            <p className="mt-7 text-lg text-muted-foreground leading-relaxed" data-testid="hq-address">{s.address}</p>
            {s.phone ? (
              <a
                href={`tel:${s.phone.replace(/\s/g, "")}`}
                data-testid="hq-phone"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                <Phone className="h-3.5 w-3.5" />
                <span dir="ltr">{s.phone}</span>
              </a>
            ) : null}
          </div>
        </Reveal>

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {offices.filter((o) => !o.is_hq).map((o, i) => (
            <Reveal key={o.id} delay={0.06 * i}>
              <div data-testid={`office-detail-${o.id}`} className="h-full border border-border bg-card/40 p-8 card-lift" style={{ borderRadius: "var(--radius)" }}>
                <p className="eyebrow mb-3">{L(o.kind) || t("rep_badge")}</p>
                <h3 className="font-head text-2xl md:text-3xl">{L(o.country)}</h3>
                {L(o.city) ? <p className="mt-1 text-sm text-muted-foreground">{L(o.city)}</p> : null}
                {L(o.address) ? <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{L(o.address)}</p> : null}
                {o.phone ? (
                  <a
                    href={`tel:${o.phone.replace(/\s/g, "")}`}
                    data-testid={`office-phone-${o.id}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    <span dir="ltr">{o.phone}</span>
                  </a>
                ) : null}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-card/20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
          <SectionHead eyebrow={t("map_eyebrow")} title={t("map_title")} sub={t("map_sub")} testid="map-section-head" />
          <PresenceMap />
        </div>
      </section>
    </div>
  );
}
